import { useCallback, useEffect, useMemo, useState } from "react";

import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  Building2,
  CheckCircle2,
  LandPlot,
  Loader2,
  MapPin,
} from "lucide-react";

import { getAPIPayload } from "../../../apis/getApi";
import { postAPIPayload } from "../../../apis/postApi";
import {
  buildBillingPrefillFromIdMailingAddress,
  buildSignupPropertiesIdPayload,
  formatTitleOwnerNamesFromJson,
  persistSignupBillingPrefill,
} from "../../helpers/utils";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";

type PropertyCandidate = {
  property_id: string;
  display_address: string;
  parcel_number?: string | null;
  owner_name?: string | null;
  county?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  is_raw_land?: boolean;
  is_subject?: boolean;
  address_matched?: boolean;
  json_response: Record<string, unknown>;
  address?: string;
  street_number?: string | null;
  site_route?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  country?: string;
};

type PropertyOptionsResponse = {
  county?: string | null;
  id_mailing_address?: {
    address_line?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  subject_property?: PropertyCandidate | null;
  candidates?: PropertyCandidate[];
  default_selected_property_ids?: string[];
};

const normalizePropertyId = (value: unknown) => String(value ?? "");

const extractApiErrorMessage = (response: any, fallback: string) => {
  if (typeof response?.message === "string" && response.message.trim()) {
    return response.message;
  }

  const nestedMessage = response?.data?.message;
  if (typeof nestedMessage === "string" && nestedMessage.trim()) {
    return nestedMessage;
  }

  const validationErrors = response?.data?.errors;
  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors)
      .flat()
      .find((entry) => typeof entry === "string" && entry.trim());

    if (typeof firstError === "string") {
      return firstError;
    }
  }

  return fallback;
};

type PropertySelectionFromIdProps = {
  sessionId: string;
  buildVerifyPayload: () => Record<string, unknown>;
  onBack: () => void;
  onProtected?: () => void;
  onSearchDifferentAddress?: (
    sessionId: string,
    idMailingAddress: PropertyOptionsResponse["id_mailing_address"],
  ) => void;
  useAuthToken?: boolean;
  onApplySuccess?: (response: unknown) => void;
  mapApiUrl?: (url: string) => string;
  backLabel?: string;
  searchDifferentLabel?: string;
};

const defaultMapApiUrl = (url: string) => url;

const PropertySelectionFromId = ({
  sessionId,
  buildVerifyPayload,
  onBack,
  onProtected,
  onSearchDifferentAddress,
  useAuthToken = false,
  onApplySuccess,
  mapApiUrl = defaultMapApiUrl,
  backLabel = "Choose another method",
  searchDifferentLabel = "Search for a different property",
}: PropertySelectionFromIdProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState<PropertyOptionsResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const allProperties = useMemo(() => {
    const list: PropertyCandidate[] = [];
    if (options?.subject_property) {
      list.push(options.subject_property);
    }
    (options?.candidates || []).forEach((candidate) => {
      if (candidate.property_id !== options?.subject_property?.property_id) {
        list.push(candidate);
      }
    });
    return list;
  }, [options]);

  const alternativeProperties = useMemo(
    () => allProperties.filter((property) => !property.is_subject),
    [allProperties],
  );

  const loadOptions = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getAPIPayload({
        url: mapApiUrl(
          `properties/ep-aware-verification/${sessionId}/property-options`,
        ),
        token: useAuthToken,
      });

      if (!response?.success) {
        throw new Error(
          response?.message || "Unable to load property options for your ID.",
        );
      }

      const data = response.data as PropertyOptionsResponse;
      setOptions(data);
      setSelectedIds(
        (data.default_selected_property_ids || []).map(normalizePropertyId),
      );

      const mailingPrefill = buildBillingPrefillFromIdMailingAddress(
        data.id_mailing_address,
      );
      if (mailingPrefill) {
        persistSignupBillingPrefill(mailingPrefill);
      }
    } catch (loadError: any) {
      setError(loadError?.message || "Unable to load property options.");
    } finally {
      setLoading(false);
    }
  }, [mapApiUrl, sessionId, useAuthToken]);

  useEffect(() => {
    dispatch(userSigupAction.clearPropertyVerified({}));
    loadOptions();
  }, [dispatch, loadOptions]);

  const toggleProperty = (propertyId: string, checked: boolean) => {
    const normalizedId = normalizePropertyId(propertyId);
    const property = allProperties.find(
      (item) => normalizePropertyId(item.property_id) === normalizedId,
    );
    if (checked && property && !property.address_matched) {
      setError(
        "This property's tax-record mailing address does not match your government ID.",
      );
      return;
    }

    setSelectedIds((current) => {
      if (checked) {
        return current.some((id) => normalizePropertyId(id) === normalizedId)
          ? current
          : [...current, normalizedId];
      }
      return current.filter((id) => normalizePropertyId(id) !== normalizedId);
    });
  };

  const handleSubjectNo = () => {
    if (options?.subject_property) {
      setSelectedIds((current) =>
        current.filter((id) => id !== options.subject_property?.property_id),
      );
    }
  };

  const submitSelection = async (ids: string[]) => {
    if (ids.length === 0) {
      setError("Select at least one property to protect.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const normalizedIds = ids.map(normalizePropertyId);
      const selectedProperties = allProperties.filter((property) =>
        normalizedIds.includes(normalizePropertyId(property.property_id)),
      );

      if (selectedProperties.length === 0) {
        setError(
          "We couldn't match the selected property. Refresh the page and try again.",
        );
        return;
      }

      const unmatched = selectedProperties.filter(
        (property) => !property.address_matched,
      );
      if (unmatched.length > 0) {
        setError(
          "Each selected property must have a tax-record mailing address that matches your government ID.",
        );
        return;
      }

      const verifyPayload = buildVerifyPayload();
      if (!verifyPayload?.user_id) {
        setError(
          "We couldn't find your account details. Go back and sign in again, then retry.",
        );
        return;
      }

      const response = await postAPIPayload({
        url: mapApiUrl(
          `properties/ep-aware-verification/${sessionId}/apply-properties`,
        ),
        data: {
          ...verifyPayload,
          require_mailing_address_match: true,
          properties: selectedProperties,
        },
        token: useAuthToken,
      });

      if (response?.success) {
        if (onApplySuccess) {
          onApplySuccess(response);
          onProtected?.();
          return;
        }

        const reportRecord = {
          ...selectedProperties[0],
          risk_report_id:
            response?.data?.risk_report_id ??
            response?.data?.report_id ??
            response?.data?.id,
          report_id:
            response?.data?.report_id ??
            response?.data?.risk_report_id ??
            response?.data?.id,
        };

        dispatch(
          userSigupAction.setPropertiesId(
            buildSignupPropertiesIdPayload([reportRecord]),
          ),
        );
        dispatch(userSigupAction.fetchVerifedAnswersData(response));
        onProtected?.();
        return;
      }

      dispatch(userSigupAction.fetchVerifedAnswersData(response));
      setError(
        extractApiErrorMessage(
          response,
          "Unable to protect the selected properties.",
        ),
      );
    } catch (submitError: any) {
      setError(
        submitError?.message || "Unable to protect the selected properties.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubjectYesAndContinue = async () => {
    if (!options?.subject_property) {
      return;
    }

    if (!options.subject_property.address_matched) {
      setError(
        "This property's tax-record mailing address does not match your government ID. Search for a different property instead.",
      );
      return;
    }

    const subjectId = options.subject_property.property_id;
    const ids = selectedIds.includes(subjectId)
      ? selectedIds
      : [...selectedIds, subjectId];

    setSelectedIds(ids);
    await submitSelection(ids);
  };

  const handleContinue = async () => {
    await submitSelection(selectedIds);
  };

  const renderPropertyCard = (property: PropertyCandidate) => {
    const propertyId = normalizePropertyId(property.property_id);
    const checked = selectedIds.some(
      (id) => normalizePropertyId(id) === propertyId,
    );
    const ownerDisplayName = formatTitleOwnerNamesFromJson(property);

    return (
      <div
        key={property.property_id}
        role="button"
        tabIndex={0}
        onClick={() => toggleProperty(propertyId, !checked)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleProperty(propertyId, !checked);
          }
        }}
        className={`flex items-start gap-4 rounded-2xl border p-4 cursor-pointer transition-all ${
          checked
            ? "border-secondary bg-secondary/5 shadow-sm"
            : "border-border hover:border-secondary/50"
        }`}
      >
        <div
          className="shrink-0 mt-1"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Checkbox
            checked={checked}
            onCheckedChange={(value) =>
              toggleProperty(propertyId, value === true)
            }
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            {property.is_raw_land ? (
              <LandPlot className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
            ) : (
              <Building2 className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-medium text-foreground">
                {property.display_address}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {property.is_raw_land
                  ? "Raw land / parcel"
                  : "Improved property"}
                {property.parcel_number
                  ? ` • Parcel ${property.parcel_number}`
                  : ""}
              </p>
              {ownerDisplayName ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Owner: {ownerDisplayName}
                </p>
              ) : null}
              {property.address_matched ? (
                <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mailing address matches your ID
                </p>
              ) : (
                <p className="text-xs text-amber-700 mt-2">
                  Tax-record mailing address does not match your ID
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground gap-3">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading properties linked to your ID...
      </div>
    );
  }

  const hasSelectableProperties = allProperties.some(
    (property) => property.address_matched,
  );

  if (allProperties.length === 0 || !hasSelectableProperties) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary/10 text-secondary mb-4">
            <MapPin className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-display font-medium text-foreground mb-2">
            No matching properties at your ID address
          </h2>
          <p className="text-sm text-muted-foreground">
            We searched county records for properties at the mailing address on
            your government ID and did not find a match
            {options?.county ? ` in ${options.county}` : ""}.
          </p>
          {options?.id_mailing_address?.address_line ? (
            <p className="text-xs text-muted-foreground mt-2">
              ID mailing address: {options.id_mailing_address.address_line}
              {options.id_mailing_address.city
                ? `, ${options.id_mailing_address.city}`
                : ""}
              {options.id_mailing_address.state
                ? `, ${options.id_mailing_address.state}`
                : ""}
              {options.id_mailing_address.zip
                ? ` ${options.id_mailing_address.zip}`
                : ""}
            </p>
          ) : null}
        </div>

        <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground text-center space-y-2">
          <p>
            You can search for another property anywhere in the US. The county
            tax-record mailing address on that property must match your ID
            mailing address before you can continue.
          </p>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button type="button" variant="outline" onClick={onBack}>
            {backLabel}
          </Button>
          {onSearchDifferentAddress ? (
            <Button
              type="button"
              onClick={() =>
                onSearchDifferentAddress(
                  sessionId,
                  options?.id_mailing_address ?? null,
                )
              }
            >
              {searchDifferentLabel}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary/10 text-secondary mb-4">
          <MapPin className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-display font-medium text-foreground mb-2">
          Which properties do you want to protect?
        </h2>
        <p className="text-sm text-muted-foreground">
          Your ID is verified. Select properties whose tax-record mailing
          address matches your government ID
          {options?.county ? ` in ${options.county}` : ""}.
        </p>
        {options?.id_mailing_address?.address_line ? (
          <p className="text-xs text-muted-foreground mt-2">
            ID address: {options.id_mailing_address.address_line}
            {options.id_mailing_address.city
              ? `, ${options.id_mailing_address.city}`
              : ""}
            {options.id_mailing_address.state
              ? `, ${options.id_mailing_address.state}`
              : ""}
            {options.id_mailing_address.zip
              ? ` ${options.id_mailing_address.zip}`
              : ""}
          </p>
        ) : null}
      </div>

      {options?.subject_property ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <h3 className="text-sm font-semibold text-foreground">
            Property you searched
          </h3>
          {renderPropertyCard(options.subject_property)}
          {alternativeProperties.length > 0 ? (
            <div className="flex flex-wrap gap-3 justify-center">
              <Button type="button" variant="outline" onClick={handleSubjectNo}>
                No, show other matches
              </Button>
              <Button
                type="button"
                onClick={handleSubjectYesAndContinue}
                disabled={submitting}
              >
                {submitting
                  ? "Protecting property..."
                  : "Yes, protect this property"}
              </Button>
            </div>
          ) : null}
        </motion.div>
      ) : null}

      {alternativeProperties.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <h3 className="text-sm font-semibold text-foreground">
            Other properties matching your ID mailing address
            {options?.county ? ` in ${options.county}` : ""}
          </h3>
          <p className="text-xs text-muted-foreground">
            You can select one or multiple properties, including raw land
            parcels.
          </p>
          <div className="space-y-3">
            {alternativeProperties.map((property) =>
              renderPropertyCard(property),
            )}
          </div>
        </motion.div>
      ) : null}

      {alternativeProperties.length === 0 && options?.subject_property ? (
        <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground text-center">
          No other properties with a matching mailing address were found. You
          can protect the property above if its tax-record mailing address
          matches your ID.
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-3">
        {onSearchDifferentAddress ? (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              onSearchDifferentAddress(
                sessionId,
                options?.id_mailing_address ?? null,
              )
            }
          >
            {searchDifferentLabel}
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onBack}>
          {backLabel}
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={submitting || selectedIds.length === 0}
        >
          {submitting
            ? "Protecting selected properties..."
            : selectedIds.length === 1 && !alternativeProperties.length
              ? "Protect this property"
              : `Protect selected (${selectedIds.length})`}
        </Button>
      </div>
    </div>
  );
};

export default PropertySelectionFromId;
